<template>
  <div class="admin-reports">
    <div
      v-if="!isAdmin"
      class="text-center text-muted py-3"
    >
      No tienes permisos para ver este reporte.
    </div>

    <template v-else>
      <b-row class="align-items-center mb-2">
        <b-col>
          <h1 class="font-weight-bolder mb-0">
            Reportes
          </h1>
          <small class="text-muted">Clientes considerados: {{ customersConsidered }}</small>
        </b-col>

        <b-col
          cols="12"
          md="4"
          lg="3"
          class="mt-1 mt-md-0"
        >
          <div class="d-flex align-items-center">
            <b-form-input
              v-model.number="year"
              type="number"
              min="2020"
              step="1"
              class="mr-1"
              @keyup.enter="loadReport"
            />
            <b-button
              variant="primary"
              :disabled="loading"
              @click="loadReport"
            >
              Cargar
            </b-button>
          </div>
          <b-form-checkbox
            v-model="excludeSpecialCustomer"
            switch
            class="mt-1"
            :disabled="loading"
            @change="loadReport"
          >
            Excluir cliente Andres
          </b-form-checkbox>
        </b-col>
      </b-row>

      <b-overlay
        :show="loading"
        opacity="0.12"
      >
        <b-alert
          v-if="errorMessage"
          show
          variant="danger"
          class="mb-2"
        >
          {{ errorMessage }}
        </b-alert>

        <b-card
          v-if="weeks.length && orderedRows.length"
          no-body
          class="mb-2"
        >
          <div class="table-responsive">
            <table class="table table-sm mb-0 report-table">
              <thead>
                <tr class="month-row">
                  <th class="label-col" />
                  <th
                    v-for="(group, index) in monthHeaderCells"
                    :key="`month-${index}`"
                    :colspan="group.span"
                    class="text-center"
                  >
                    {{ group.label }}
                  </th>
                </tr>
                <tr>
                  <th class="label-col">
                    Indicador
                  </th>
                  <th
                    v-for="week in weeks"
                    :key="`week-header-${week.index}`"
                    class="text-center"
                  >
                    S{{ week.index }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="row in orderedRows"
                  :key="row.key"
                  :class="rowColorClass(row.key)"
                >
                  <td class="label-col font-weight-bold">
                    {{ row.label }}
                  </td>
                  <td
                    v-for="week in weeks"
                    :key="`${row.key}-${week.index}`"
                    class="text-center"
                  >
                    {{ valueFor(row, week.index) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>

        <b-alert
          v-else-if="!loading"
          show
          variant="light"
        >
          No hay datos para el año seleccionado.
        </b-alert>

        <b-row v-if="weeks.length">
          <b-col
            cols="12"
            xl="6"
            class="mb-2"
          >
            <b-card>
              <b-card-title>Estatus usuarios por semana</b-card-title>
              <apexchart
                type="line"
                height="300"
                :options="statusLineOptions"
                :series="statusLineSeries"
              />
            </b-card>
          </b-col>

          <b-col
            cols="12"
            xl="6"
            class="mb-2"
          >
            <b-card>
              <b-card-title>Usuarios que cotizaron</b-card-title>
              <apexchart
                type="bar"
                height="300"
                :options="purchaseBarOptions"
                :series="purchaseBarSeries"
              />
            </b-card>
          </b-col>
        </b-row>
      </b-overlay>
    </template>
  </div>
</template>

<script>
import axios from '@axios'

export default {
  name: 'AdminReports',
  data() {
    return {
      loading: false,
      year: 2026,
      excludeSpecialCustomer: false,
      errorMessage: '',
      reportData: null,
      rowOrder: [
        'activado',
        'retenido',
        'churn',
        'usuarios_que_cotizaron',
        'si_compraron',
        'no_compraron',
      ],
    }
  },
  computed: {
    visibleWeekLimit() {
      const selectedYear = Number(this.year)
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()

      if (selectedYear === currentYear) {
        return this.isoWeekNumber(currentDate)
      }

      return this.maxAvailableWeek
    },
    maxAvailableWeek() {
      const weeks = Array.isArray(this.reportData?.weeks) ? this.reportData.weeks : []
      return weeks.reduce((max, week) => Math.max(max, Number(week?.index) || 0), 0)
    },
    isAdmin() {
      const user = this.$store.getters['auth/currentUser'] || {}
      return user.role_name === 'admin'
    },
    weeks() {
      const weeks = Array.isArray(this.reportData?.weeks) ? this.reportData.weeks : []
      return weeks.filter(week => Number(week?.index) <= this.visibleWeekLimit)
    },
    customersConsidered() {
      return Number(this.reportData?.customers_considered || 0)
    },
    rowsMap() {
      const rows = Array.isArray(this.reportData?.rows) ? this.reportData.rows : []
      return rows.reduce((acc, row) => {
        if (row?.key) acc[row.key] = row
        return acc
      }, {})
    },
    orderedRows() {
      const rows = []
      const used = new Set()

      this.rowOrder.forEach(key => {
        const row = this.rowsMap[key]
        if (row) {
          rows.push(row)
          used.add(key)
        }
      })

      Object.keys(this.rowsMap).forEach(key => {
        if (!used.has(key)) rows.push(this.rowsMap[key])
      })

      return rows
    },
    monthHeaderCells() {
      const apiGroups = Array.isArray(this.reportData?.month_groups) ? this.reportData.month_groups : []
      if (apiGroups.length) {
        return apiGroups.reduce((acc, group) => {
          const fromWeek = Number(group.from_week)
          const toWeek = Number(group.to_week)
          const visibleFrom = Math.max(fromWeek, 1)
          const visibleTo = Math.min(toWeek, this.visibleWeekLimit)
          const span = visibleTo - visibleFrom + 1

          if (span > 0) {
            acc.push({
              label: group.month,
              span,
            })
          }

          return acc
        }, [])
      }

      const cells = []
      this.weeks.forEach(week => {
        const label = week.month || ''
        const prev = cells[cells.length - 1]
        if (!prev || prev.label !== label) {
          cells.push({ label, span: 1 })
          return
        }
        prev.span += 1
      })
      return cells
    },
    weekLabels() {
      return this.weeks.map(week => `S${week.index}`)
    },
    statusLineSeries() {
      const keys = ['activado', 'retenido', 'churn']
      return keys.map(key => ({
        name: this.rowsMap[key]?.label || key,
        data: this.weeks.map(week => this.valueFor(this.rowsMap[key], week.index)),
      }))
    },
    purchaseBarSeries() {
      const keys = ['si_compraron', 'no_compraron']
      return keys.map(key => ({
        name: this.rowsMap[key]?.label || key,
        data: this.weeks.map(week => this.valueFor(this.rowsMap[key], week.index)),
      }))
    },
    statusLineOptions() {
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        stroke: {
          curve: 'smooth',
          width: 3,
        },
        markers: {
          size: 4,
        },
        colors: ['#74c043', '#00c0ef', '#ea5455'],
        xaxis: {
          categories: this.weekLabels,
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            formatter: value => Math.round(value),
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          position: 'top',
        },
      }
    },
    purchaseBarOptions() {
      return {
        chart: {
          toolbar: { show: false },
          stacked: false,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '45%',
          },
        },
        colors: ['#74c043', '#ff7f50'],
        xaxis: {
          categories: this.weekLabels,
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            formatter: value => Math.round(value),
          },
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          position: 'top',
        },
      }
    },
  },
  created() {
    if (this.isAdmin) {
      this.loadReport()
    }
  },
  methods: {
    isoWeekNumber(date) {
      const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      const day = utcDate.getUTCDay() || 7
      utcDate.setUTCDate(utcDate.getUTCDate() + 4 - day)
      const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1))
      return Math.ceil((((utcDate - yearStart) / 86400000) + 1) / 7)
    },
    valueFor(row, weekIndex) {
      if (!row || !Array.isArray(row.weeks)) return 0
      const found = row.weeks.find(item => Number(item.week) === Number(weekIndex))
      return Number(found?.value || 0)
    },
    rowColorClass(key) {
      if (key === 'activado') return 'row-activado'
      if (key === 'retenido') return 'row-retenido'
      if (key === 'churn') return 'row-churn'
      if (key === 'usuarios_que_cotizaron') return 'row-cotizaron'
      return ''
    },
    async loadReport() {
      this.loading = true
      this.errorMessage = ''

      try {
        const selectedYear = Number(this.year || 2026)
        const params = { year: selectedYear }
        if (this.excludeSpecialCustomer) {
          params.exclude_special_customer = true
        }

        const { data } = await axios.get('/v1/reports/quotes_purchased_lifecycle_weekly_january', {
          params,
        })
        this.reportData = data || {}
      } catch (error) {
        const responseData = error?.response?.data || {}
        this.errorMessage = responseData.message || responseData.error || 'No se pudo cargar el reporte'
        this.reportData = null
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.admin-reports {
  display: flex;
  flex-direction: column;
}

.report-table {
  th,
  td {
    vertical-align: middle;
    border: 1px solid #d9d9d9;
  }

  th {
    font-weight: 700;
  }
}

.label-col {
  min-width: 210px;
  text-align: left;
}

.month-row {
  th {
    background: #f7f7f7;
    text-transform: uppercase;
    font-size: 0.78rem;
  }
}

.row-activado {
  background-color: #c9deab;
}

.row-retenido {
  background-color: #bdd5ee;
}

.row-churn {
  background-color: #f5ceb1;
}

.row-cotizaron {
  background-color: #ececec;
}
</style>
